import {
  BanknotesIcon,
  ChartBarIcon,
  ArrowTrendingDownIcon,
  CheckIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import OpenBankingAnimation from './components/OpenBankingAnimation';
import TestimonialCarousel from '../../components/TestimonialCarousel';
import testimonials from '../../data/testimonials';


export default function Index() {
  return (
    <>
      {/* Hero Section */}
      <div className="relative bg-white overflow-hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-8 pb-12 md:pt-16 md:pb-20 lg:pt-20 lg:pb-28">
            {/* Content */}
            <div className="text-center">
              <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl leading-tight">
                <span className="block">Simple, secure</span>
                <span className="block text-emerald-600">payment links for all</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto">
                Accept payments online with ease. No complicated setup, no hidden fees.
                Just a straightforward way to get paid.
              </p>

              {/* Trust Indicators */}
              <div className="mt-8 flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-8">
                <div className="flex items-center justify-center">
                  <LockClosedIcon className="h-6 w-6 text-emerald-600 mr-2" />
                  <span className="text-gray-600">Open Banking</span>
                </div>
                <div className="flex items-center justify-center">
                  <ArrowTrendingDownIcon className="h-6 w-6 text-emerald-600 mr-2" />
                  <span className="text-gray-600">Lower Fees</span>
                </div>
              </div>

              {/* Open Banking Animation */}
              <div className="mt-12 mx-auto max-w-md">
                <OpenBankingAnimation />
              </div>

              {/* CTA Button */}
              <div className="mt-10">
                <a
                  href="/user/sign_up"
                  className="inline-block px-8 py-4 border border-transparent text-base font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 transition-colors duration-200 shadow-md"
                >
                  Start accepting payments
                </a>
                <p className="mt-3 text-sm text-gray-500">No credit card required</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section - Simplified */}
      <div id="features" className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Faster payments, without the middleman
            </h2>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
              No more wondering when you'll get your money or how much you'll receive after fees. You keep 100% of every payment.
            </p>
          </div>

          <div className="space-y-8">
            {/* Feature 1 */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-emerald-100 text-emerald-600">
                  <BanknotesIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Faster Payment</h3>
                  <p className="mt-1 text-base text-gray-500">
                    Get paid instantly into your bank. No waiting for days for third-party payouts.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-emerald-100 text-emerald-600">
                  <ArrowTrendingDownIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Lower Fees</h3>
                  <p className="mt-1 text-base text-gray-500">
                    Transaction fees are a fraction of the cost of other payment providers.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-emerald-100 text-emerald-600">
                  <LockClosedIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Open Banking</h3>
                  <p className="mt-1 text-base text-gray-500">
                    Bank-level security and fraud protection keep you and your customers safe.
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-md bg-emerald-100 text-emerald-600">
                  <ChartBarIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">Simple Analytics</h3>
                  <p className="mt-1 text-base text-gray-500">
                    Track your payments and understand your business with clear, actionable insights.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonial Section - Carousel */}
      <div id="testimonials" className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900">Trusted by individuals and businesses everywhere</h2>
          </div>
          <TestimonialCarousel testimonials={testimonials} className="py-4" />
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">Simple, transparent pricing</h2>
            <p className="mt-4 text-lg text-gray-600">
              No hidden fees, no surprises. Just straightforward pricing that scales with your business.
            </p>
          </div>

          <div className="grid md:grid-cols-1 gap-8 max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900">Simple Pricing</h3>
                <div className="mt-4 flex items-baseline">
                  <span className="text-5xl font-extrabold text-gray-900">30p</span>
                  <span className="ml-1 text-xl text-gray-500">per transaction</span>
                </div>
                <p className="mt-2 text-lg text-emerald-600 font-medium">First 10 transactions free</p>
                <p className="mt-5 text-gray-500">Perfect for businesses of all sizes.</p>
              </div>
              <div className="px-6 pt-6 pb-8">
                <ul className="space-y-4">
                  {[
                    'First 10 transactions free',
                    '30p per transaction after',
                    'No monthly fees',
                    'Unlimited transactions',
                    'Simple analytics',
                    'Payment links',
                    'Payment QR codes',
                    'Custom checkout'
                  ].map((feature) => (
                    <li key={feature} className="flex">
                      <CheckIcon className="h-5 w-5 text-emerald-500 flex-shrink-0 mr-3" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <a
                    href="/user/sign_up"
                    className="block w-full bg-emerald-600 text-white text-center px-4 py-3 rounded-md font-medium hover:bg-emerald-700 transition-colors"
                  >
                    Join the waitlist
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Simple CTA */}
      <div className="bg-gray-100 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">Ready to get started?</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
              Join thousands of individuals and businesses that trust Bare for their payment needs.
            </p>
            <div className="mt-8">
              <a
                href="/user/sign_up"
                className="inline-block px-8 py-4 border border-transparent text-base font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 transition-colors duration-200 shadow-md"
              >
                Join the waitlist
              </a>
              <p className="mt-3 text-sm text-gray-500">No payment required. Set up in minutes.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
